import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft, faCheck } from "@fortawesome/free-solid-svg-icons";
import { ACTOR_TYPE_OPTIONS, SORT_OPTIONS } from "./activityLogUtils";

const ActivityLogFilters = ({
  filters,
  onFilterChange,
  onReset,
  sort,
  onSortChange,
}) => {
  // Local draft state for text inputs — committed to the parent only on
  // Enter key or the Apply button, to avoid firing an API request on every
  // keystroke (mirrors the StaffFilters search pattern).
  const [actionDraft, setActionDraft] = useState(filters.action || "");
  const [targetTypeDraft, setTargetTypeDraft] = useState(filters.targetType || "");

  // Keep local drafts in sync when the parent resets / clears filters.
  useEffect(() => {
    setActionDraft(filters.action || "");
    setTargetTypeDraft(filters.targetType || "");
  }, [filters.action, filters.targetType]);

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    onFilterChange(field, value);
  };

  const commitText = (field, value) => {
    onFilterChange(field, value);
  };

  const applyAllText = () => {
    onFilterChange("action", actionDraft);
    onFilterChange("targetType", targetTypeDraft);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {/* Actor Type */}
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Actor Type
          </label>
          <select
            className="form-select mt-1 w-full"
            value={filters.actorType || ""}
            onChange={handleInputChange("actorType")}
          >
            {ACTOR_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Action */}
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Action
          </label>
          <input
            type="text"
            className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none placeholder:text-slate-400 focus:border-slate-400 focus:bg-white"
                        placeholder="e.g. Created, Updated, Deleted"
            value={actionDraft}
            onChange={(e) => setActionDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitText("action", actionDraft);
            }}
          />
        </div>

        {/* Target Type */}
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Target Type
          </label>
          <input
            type="text"
            className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none placeholder:text-slate-400 focus:border-slate-400 focus:bg-white"
                        placeholder="e.g. StaffMember, Course"
            value={targetTypeDraft}
            onChange={(e) => setTargetTypeDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitText("targetType", targetTypeDraft);
            }}
          />
        </div>

        {/* Date range */}
        <div className="sm:col-span-2 lg:col-span-2">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Date Range
          </label>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="date"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none focus:border-slate-400 focus:bg-white"
              value={filters.from || ""}
              onChange={handleInputChange("from")}
              max={new Date().toISOString().split("T")[0]}
            />
            <span className="text-slate-400">→</span>
            <input
              type="date"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none focus:border-slate-400 focus:bg-white"
              value={filters.to || ""}
              onChange={handleInputChange("to")}
              max={new Date().toISOString().split("T")[0]}
            />
          </div>
        </div>
            </div>

      {/* Sort + Apply + Reset */}
      <div className="mt-3 flex items-end justify-between gap-2 border-t border-slate-200 pt-3">
        <div className="flex items-center gap-2">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Sort
          </label>
          <select
            className="form-select"
            style={{ minWidth: "150px" }}
            value={sort}
            onChange={(e) => onSortChange(e.target.value)}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={applyAllText}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-600 shadow-sm transition hover:bg-slate-50"
          >
            <FontAwesomeIcon icon={faCheck} />
            Apply
          </button>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-600 shadow-sm transition hover:bg-slate-50"
          >
            <FontAwesomeIcon icon={faRotateLeft} />
            Reset
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogFilters;